import { DarwinAgent, PromptProto, DarwinTool } from "build-ts/darwin_agent";
import showToast from "hooks/useCustomToast";
import { getCustomAgent } from "./CustomAgentServices";
import { getTreeById } from "./AnalysisTreeServices";
import { getPlan } from "./AnalysisPlanServices";
import { getScheduledJobRun, getScheduledJob } from "./ScheduleServices";
import { ChatRequest, ChatResponse } from "build-ts/chat_session";
import { JinjaEnv } from "build-ts/jinja";
import { getAnalyst } from "./AnalystService";

const getApiErrorMessage = (errorData: any, response: Response): string =>
    errorData?.errorMsg ||
    errorData?.error ||
    errorData?.detail ||
    errorData?.message ||
    `HTTP ${response.status}: ${response.statusText}`;

export const getAgentsList = async (params: URLSearchParams) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_NEW_EVA_AGENT_LIST}?${params}`, {
            method: "GET",
            credentials: "include",
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(getApiErrorMessage(errorData, response));
        }

        return await response.json();
    } catch (error: any) {
        console.error("Error fetching agents list:", error);
        showToast("Failed to fetch agents list", error?.message, "error");
        // Re-throw error for table components to handle with error pages
        throw error;
    }
};

export const getAgentById = async (agentId: string) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_NEW_EVA_AGENT}/${agentId}`, {
            method: "GET",
            credentials: "include",
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(getApiErrorMessage(errorData, response));
        }

        return await response.json();
    } catch (error) {
        console.error("Error fetching agent data:", error);
        const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
        showToast("Failed to fetch agent data", errorMessage, "error");
        return null;
    }
};

export const createNewAgent = async (agentData: DarwinAgent) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_NEW_EVA_AGENT}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify(agentData),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(getApiErrorMessage(errorData, response));
        }

        return await response.json();
    } catch (error) {
        console.error("Error creating agent:", error);
        const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
        showToast("Failed to create agent", errorMessage, "error");
        return null;
    }
};

export const updateAgent = async (agent_id: string | undefined, agentConfig: DarwinAgent, selectedTools: DarwinTool[]) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_NEW_EVA_AGENT}/${agent_id}`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify({
                header: {
                    name: agentConfig.header?.name,
                    description: agentConfig.header?.description,
                    is_active: agentConfig.header?.is_active,
                    version: agentConfig.header?.version,
                },
                tools: selectedTools.map(tool => ({ header: { id: tool.header?.id } })),
                skills: (agentConfig.skills ?? []).map(skill => ({ header: { id: skill.header?.id } })),
                context_template: agentConfig.context_template?.trim() || null,
            }),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(getApiErrorMessage(errorData, response));
        }

        const data = await response.json();
        showToast("Agent updated successfully", "", "success");
        return data;
    } catch (error: any) {
        console.error("Error updating agent:", error);
        const errorMessage = error?.message || "Unknown error occurred";
        showToast("Failed to update agent", errorMessage, "error");
        return null;
    }
};

export const duplicateAgents = async (agent_ids: string[]) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_NEW_EVA_AGENT_DUPLICATE}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify({ agent_ids }),
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(getApiErrorMessage(errorData, response));
        }

        const data = await response.json();
        showToast("Agent(s) duplicated successfully", "", "success");
        return data;
    } catch (error: any) {
        console.error("Error duplicating agents:", error);
        const errorMessage = error?.message || "Unknown error occurred";
        showToast("Failed to duplicate agents", errorMessage, "error");
        return null;
    }
};

export const deleteAgent = async (agentId: string) => {
    const response = await fetch(`${process.env.REACT_APP_NEW_EVA_AGENT}/${agentId}`, {
        method: "DELETE",
        credentials: "include",
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(getApiErrorMessage(errorData, response));
    }

    return true;
};

export const getToolsList = async (params: URLSearchParams) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_NEW_EVA_TOOL_LIST}?${params}`, {
            method: "GET",
            credentials: "include",
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(getApiErrorMessage(errorData, response));
        }

        return await response.json();
    } catch (error: any) {
        console.error("Error fetching tools list:", error);
        showToast("Failed to fetch tools list", error?.message, "error");
        // Re-throw error for table components to handle with error pages
        throw error;
    }
};

export const getToolById = async (toolId: string) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_NEW_EVA_TOOL}/${toolId}`, {
            method: "GET",
            credentials: "include",
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(getApiErrorMessage(errorData, response));
        }

        return await response.json();
    } catch (error) {
        console.error("Error fetching tool data:", error);
        const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
        showToast("Failed to fetch tool data", errorMessage, "error");
        return null;
    }
};

export const updateTool = async (toolId: string | undefined, toolData: DarwinTool) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_NEW_EVA_TOOL}/${toolId}`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify({
                header: {
                    name: toolData.header?.name,
                    description: toolData.header?.description,
                },
                agentic_name: toolData?.agentic_name,
                agentic_description: toolData?.agentic_description,
                // Optional template for skill tools
                template: (toolData as any)?.template,
            }),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(getApiErrorMessage(errorData, response));
        }

        const data = await response.json();
        showToast("Tool updated successfully", "", "success");
        return data;
    } catch (error: any) {
        console.error("Error updating tool:", error);
        const errorMessage = error?.message || "Unknown error occurred";
        showToast("Failed to update tool", errorMessage, "error");
        return null;
    }
};

export const duplicateTools = async (tool_ids: number[]) => {
    try {
        const queryParams = new URLSearchParams();
        for (const toolId of tool_ids) {
            queryParams.append("tool_ids", toolId.toString());
        }

        const response = await fetch(
            `${process.env.REACT_APP_NEW_EVA_TOOL_DUPLICATE}?${queryParams.toString()}`,
            {
            method: "POST",
            credentials: "include",
            }
        );
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(getApiErrorMessage(errorData, response));
        }

        const data = await response.json();
        showToast("Tool(s) duplicated successfully", "", "success");
        return data;
    } catch (error: any) {
        console.error("Error duplicating tools:", error);
        const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
        showToast("Failed to duplicate tools", errorMessage, "error");
        return null;
    }
};

// Skill service functions
export const getSkillsList = async (params: URLSearchParams) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_NEW_EVA_SKILL_LIST}?${params}`, {
            method: "GET",
            credentials: "include",
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(getApiErrorMessage(errorData, response));
        }

        return await response.json();
    } catch (error: any) {
        console.error("Error fetching skills list:", error);
        showToast("Failed to fetch skills list", error?.message, "error");
        throw error;
    }
};

export const getSkillById = async (skillId: string) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_NEW_EVA_SKILL}/${skillId}`, {
            method: "GET",
            credentials: "include",
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(getApiErrorMessage(errorData, response));
        }

        return await response.json();
    } catch (error) {
        console.error("Error fetching skill data:", error);
        const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
        showToast("Failed to fetch skill data", errorMessage, "error");
        return null;
    }
};

export const createSkill = async (skillData: any) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_NEW_EVA_SKILL}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify(skillData),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(getApiErrorMessage(errorData, response));
        }

        const data = await response.json();
        showToast("Skill created successfully", "", "success");
        return data;
    } catch (error) {
        console.error("Error creating skill:", error);
        const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
        showToast("Failed to create skill", errorMessage, "error");
        return null;
    }
};

export const updateSkill = async (skillId: string | undefined, skillData: any, selectedTools: DarwinTool[]) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_NEW_EVA_SKILL}/${skillId}`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify({
                header: {
                    name: skillData.header?.name,
                    description: skillData.header?.description,
                },
                agentic_name: skillData.agentic_name,
                tools: selectedTools.map((tool: DarwinTool) => ({ header: { id: tool.header?.id } })),
            }),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(getApiErrorMessage(errorData, response));
        }

        const data = await response.json();
        showToast("Skill updated successfully", "", "success");
        return data;
    } catch (error: any) {
        console.error("Error updating skill:", error);
        const errorMessage = error?.message || "Unknown error occurred";
        showToast("Failed to update skill", errorMessage, "error");
        return null;
    }
};

export const deleteSkill = async (skillId: string) => {
    const response = await fetch(`${process.env.REACT_APP_NEW_EVA_SKILL}/${skillId}`, {
        method: "DELETE",
        credentials: "include",
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(getApiErrorMessage(errorData, response));
    }

    return true;
};

// prompt list api
export const getAssignedPrompts = async (agentId: string) => {
    const response = await fetch(`${process.env.REACT_APP_NEW_EVA_PROMPT}/agent/${agentId}`, {
      method: "GET",
      credentials: "include",
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(getApiErrorMessage(errorData, response));
    }

    return await response.json();
};

export const getAssignedSkillPrompts = async (skillId: string) => {
    const response = await fetch(`${process.env.REACT_APP_NEW_EVA_PROMPT}/skill/${skillId}`, {
      method: "GET",
      credentials: "include",
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(getApiErrorMessage(errorData, response));
    }

    return await response.json();
};

// Get Prompt Detail by Id
export const getPromptByid = async (promptId: string) => {
    const response = await fetch(`${process.env.REACT_APP_NEW_EVA_PROMPT}/${promptId}`, {
      method: "GET",
      credentials: "include",
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(getApiErrorMessage(errorData, response));
    }

    return await response.json();
};

// Update a Prompt
export const updatePrompt = async (
    promptId: string,
    promptData: PromptProto,
    isSkill: boolean = false
) => {
    const params = new URLSearchParams();
    params.append("is_skill", String(isSkill));
    const requestBody: any = {
        header: {
            description: promptData.header?.description,
            is_active: promptData.header?.is_active,
        },
        template: promptData.template,
    };
    if (!isSkill) {
        requestBody.model_config = {
            model_name: promptData.model_config?.model_name,
            model_provider: promptData.model_config?.model_provider,
            temperature: promptData.model_config?.temperature,
            reasoning_effort_level: promptData.model_config?.reasoning_effort_level,
        };
    }
    const response = await fetch(`${process.env.REACT_APP_NEW_EVA_PROMPT}/${promptId}?${params.toString()}`, {
        method: "PUT",
        headers: {
        "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(getApiErrorMessage(errorData, response));
    }

    return await response.json();
};

// Duplicate a Prompt
export const duplicatePrompt = async (promptId: string, isSkill: boolean = false) => {
    const params = new URLSearchParams();
    params.append("is_skill", String(isSkill));
    const response = await fetch(`${process.env.REACT_APP_NEW_EVA_PROMPT}/${promptId}/duplicate?${params.toString()}`, {
        method: "POST",
        credentials: "include",
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(getApiErrorMessage(errorData, response));
    }

    return await response.json();
};

// Delete a Prompt
export const deletePrompt = async (promptId: string) => {
    const response = await fetch(`${process.env.REACT_APP_NEW_EVA_PROMPT}/${promptId}`, {
        method: "DELETE",
        credentials: "include",
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(getApiErrorMessage(errorData, response));
    }

    return true;
};

// Get System Prompt Documentation
export const getSystemPromptDocumentation = async (): Promise<JinjaEnv | null> => {
    try{
        const response = await fetch(`${process.env.REACT_APP_NEW_EVA_DOC}`, {
            method: "GET",
            credentials: "include",
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(getApiErrorMessage(errorData, response));
        }
        const data = await response.json();

        return JinjaEnv.fromJSON(data);
    } catch (error: any) {
        console.error("Error fetching documentation:", error);
        const errorMessage = error?.message || "Unknown error occurred";
        showToast("Failed to fetch documentation", errorMessage, "error");
        return null;
    }
};

// Validate Template Syntax
export const validateTemplateSyntax = async (content: string, sampleData: Record<string, any> = {}) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_NEW_EVA_TEMPLATE_VALIDATE}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify({
                content,
                sample_data: sampleData
            }),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(getApiErrorMessage(errorData, response));
        }
        return await response.json();
    } catch (error: any) {
        console.error("Error validating template syntax:", error);
        const errorMessage = error?.message || "Unknown error occurred";
        showToast("Validation Failed", errorMessage, "error");
        return null;
    }
};

// Generate System prompt using Agent API
export const generateSystemPrompt = async (chatRequest: ChatRequest) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_CHAT_MESSAGE}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify(ChatRequest.toJSON(chatRequest)),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(getApiErrorMessage(errorData, response));
        }

        const data = await response.json();
        return ChatResponse.fromJSON(data);
    } catch (error: any) {
        console.error("Error generating system prompt:", error);
        const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
        showToast("Failed to generate system prompt", errorMessage, "error");
        return null;
    }
};

// Fetch data for selected items from each step
export const fetchStepData = async (stepType: string, selectedIds: string[]) => {
    try {
        switch (stepType) {
            case "darwin_agents":
                // No need to fetch agent data - API only needs IDs
                return selectedIds.map(id => ({ id, name: `Agent ${id}` }));
            case "prompts":
                // No need to fetch prompt data - API only needs IDs
                return selectedIds.map(id => ({ id, name: `Prompt ${id}` }));
            case "analyses":
                // Fetch analysis trees using Newton API
                return await Promise.all(selectedIds.map(async id => {
                    const tree = await getTreeById(id, true, false, false, false);
                    return tree || null;
                }));
            case "plans":
                // Fetch analysis plans using Newton API
                return await Promise.all(selectedIds.map(async id => {
                    const plan = await getPlan(id);
                    return plan || null;
                }));
            case "custom_agents":
                // Fetch custom agents using Newton API
                return await Promise.all(selectedIds.map(async id => {
                    const custom_agents = await getCustomAgent(id);
                    return custom_agents || null;
                }));
            case "agent_runs":
                // Fetch custom agent runs using Newton API
                return await Promise.all(selectedIds.map(async id => {
                    const jobRun = await getScheduledJobRun(id);
                    return jobRun || null;
                }));
            case "analysts":
                // Fetch analysts using Newton API
                return await Promise.all(selectedIds.map(async id => {
                    const analyst = await getAnalyst(id);
                    return analyst || null;
                }));
            case "jobs":
                // Fetch scheduled jobs using Newton API
                return await Promise.all(selectedIds.map(async id => {
                    const job = await getScheduledJob(id);
                    return job || null;
                }));
            default:
                return [];
        }
    } catch (error) {
        console.error(`Error fetching ${stepType} data:`, error);
        return [];
    }
};


// Experiment APIs

// Trigger Eva Experiment runs.
export const runExperiment = async (request: ChatRequest, experiment_name: string, file: File): Promise<any> => {
    try {
      const formData = new FormData();
      const requestBody: any = ChatRequest.toJSON(request);
      formData.append("request_body", JSON.stringify(requestBody));
      formData.append("file", file);

      // Add experiment_name as a query parameter (not in FormData)
      const url = new URL(`${process.env.REACT_APP_EXPERIMENT_RUN}`);
      url.searchParams.append("experiment_name", experiment_name);

      const response = await fetch(url.toString(), {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(getApiErrorMessage(errorData, response));
      }

      // Backend is expected to return some JSON payload (e.g. job id / status)
      return await response.json();
    } catch (error: any) {
      showToast("Failed to start experiment", error?.message || "Unknown error occurred", "error");
      throw error;
    }
};

// Get Experiments List
export const getExperimentsList = async (params: URLSearchParams) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_EXPERIMENT_LIST}?${params}`, {
            method: "GET",
            credentials: "include",
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(getApiErrorMessage(errorData, response));
        }
        return await response.json();
    } catch (error: any) {
        console.error("Error fetching experiments list:", error);
        showToast("Failed to fetch experiments list", error?.message, "error");
        // Re-throw error for table components to handle with error pages
        throw error;
    }
};

// Get Experiment by ID
export const getExperimentById = async (experimentId: string) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_EXPERIMENT}/${experimentId}`, {
            method: "GET",
            credentials: "include",
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(getApiErrorMessage(errorData, response));
        }

        return await response.json();
    } catch (error) {
        console.error("Error fetching experiment data:", error);
        const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
        showToast("Failed to fetch experiment data", errorMessage, "error");
        return null;
    }
};

// Update Experiment
export const updateExperiment = async (
    experimentId: string | undefined,
    experimentData: {
        header?: {
            name?: string;
            description?: string;
        };
    },
) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_EXPERIMENT}/${experimentId}`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify({
                updated_name: experimentData.header?.name,
                updated_description: experimentData.header?.description,
            }),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(getApiErrorMessage(errorData, response));
        }

        const data = await response.json();
        showToast("Experiment updated successfully", "", "success");
        return data;
    } catch (error: any) {
        console.error("Error updating experiment:", error);
        const errorMessage = error?.message || "Unknown error occurred";
        showToast("Failed to update experiment", errorMessage, "error");
        return null;
    }
};

// Delete Experiment
export const deleteExperiment = async (experimentId: string) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_EXPERIMENT}/${experimentId}`, {
            method: "DELETE",
            credentials: "include",
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(getApiErrorMessage(errorData, response));
        }

        showToast("Experiment deleted successfully", "", "success");
        return true;
    } catch (error: any) {
        console.error("Error deleting experiment:", error);
        const errorMessage = error?.message || "Unknown error occurred";
        showToast("Failed to delete experiment", errorMessage, "error");
        throw error;
    }
};

// Download Experiment Output File
export const downloadExperimentOutput = async (experimentId: string) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_EXPERIMENT}/${experimentId}/output/download`, {
            method: "GET",
            credentials: "include",
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(getApiErrorMessage(errorData, response));
        }

        // Extract filename from Content-Disposition header (set by backend FileResponse)
        const contentDisposition = response.headers.get("Content-Disposition");
        const filename = contentDisposition?.match(/filename="?(.+?)"?;?/)?.[1] || `experiment_${experimentId}_output.csv`;

        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = downloadUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();

        window.URL.revokeObjectURL(downloadUrl);
        a.remove();

        showToast("Experiment output downloaded successfully", "", "success");
    } catch (error: any) {
        console.error("Error downloading experiment output:", error);
        const errorMessage = error?.message || "Unknown error occurred";
        showToast("Failed to download experiment output", errorMessage, "error");
        throw error;
    }
};
